/**
 * 현장 사진.
 *
 * 단서를 처음 조사하면 그 물건을 **엔진에서 그대로 한 장 찍는다.** 외부 그림을
 * 그려 넣지 않는 이유는 셋이다.
 *   1. 사진이 실제 물건과 영원히 일치한다. 가구를 옮겨도 사진이 안 틀어진다.
 *   2. 단서가 마흔여덟이고 앞으로 더 늘어난다. 그림을 그리면 그때마다 밀린다.
 *   3. 크라임씬 참가자는 현장을 **카메라로 찍는다.** 그게 이 포맷의 동작이다.
 *
 * 찍는 자리는 **그 사람이 서 있던 자리**다. 정면 규격 컷이 아니라 「내가 이걸
 * 이렇게 봤다」가 되어야 나중에 수첩을 넘길 때 그 순간이 같이 떠오른다.
 *
 * 방이 밤이라 그냥 찍으면 새까맣다. 카메라에 플래시를 하나 달아 그 프레임만
 * 밝힌다. 렌더가 끝나면 즉시 뗀다 — 월드 조명에는 아무 영향이 없다.
 *
 * **본 화면과 같은 형식의 타겟에 그린다** (2026-09-28). 예전엔 8비트 sRGB·MSAA 4배 타겟에 그렸는데, 맥(ANGLE·Metal)은
 * 셰이더 파이프라인을 타겟 형식마다 따로 만든다 — 본 화면(half-float·MSAA 없음)에서 만들어 둔 것을 못 쓰고 그 자리에서
 * 재질 백여 개를 새로 만들어, 첫 단서 사진 한 장에 **2.7초** 화면이 멎었다(맥북 M2 Pro). 처음 보는 재질이 있는 방에서도
 * 장마다 100ms 넘게 멎었다. 같은 형식이면 9~15ms 다. MSAA 는 형식이 달라지므로 대신 두 배로 그려 줄인다(SSAA 2×2).
 */

import * as THREE from 'three';
import { IS_GPU, readPixels } from './gpu';

const W = 640;
const H = 400;

/** 조준용 히트박스는 사진에 잡히면 안 된다 (colorWrite 를 꺼 둔 상자들) */
function isHit(o: THREE.Object3D): boolean {
  const m = (o as THREE.Mesh).material as THREE.Material | undefined;
  return !!m && (m as THREE.MeshBasicMaterial).colorWrite === false;
}

/** 보이는 것만으로 경계 상자를 잡는다. 히트박스를 넣으면 물건이 콩알만 해진다 */
function visibleBox(group: THREE.Object3D): THREE.Box3 | null {
  const box = new THREE.Box3();
  let any = false;
  group.traverse((o) => {
    if (!(o as THREE.Mesh).isMesh || isHit(o)) return;
    box.expandByObject(o);
    any = true;
  });
  return any ? box : null;
}

export interface PhotoRig {
  /** 프롭을 한 장 찍어 data URL 로 돌려준다. 이미 찍었으면 그 장을 다시 준다 */
  shoot: (clueId: string, group: THREE.Object3D, fallback: THREE.Vector3, from: THREE.Vector3) => string;
  get: (clueId: string) => string | null;
  dispose: () => void;
}

/**
 * @param borrow 플래시로 **잠깐 빌려 쓸 조명**(이미 씬에 있는 슬롯). 없으면 플래시를 씬에 넣었다 뺀다 — 그러면 조명 개수가
 *   바뀌어 단서를 찍을 때마다 보이는 재질이 전부 다시 컴파일됐다(2026-09-26). 게임은 장식 조명 풀의 슬롯 하나를 빌려 준다
 */
/**
 * @param sceneTarget 본 화면이 씬을 그리는 타겟(pipeline 컴포저). half-float 이면 사진도 거기에 맞춘다. 없으면(낮음 화질은
 *   캔버스에 바로 그린다 — 톤매핑이 렌더러에 있어 어차피 셰이더가 다르다) 예전처럼 sRGB 타겟에 그린다
 */
export function createPhotoRig(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  borrow?: () => THREE.PointLight | undefined,
  sceneTarget?: () => THREE.WebGLRenderTarget | null | undefined,
): PhotoRig {
  const cache = new Map<string, string>();

  const rt = new THREE.WebGLRenderTarget(W, H, {
    depthBuffer: true,
    colorSpace: THREE.SRGBColorSpace,
    samples: 4,
  });
  /** 본 화면 형식(half-float)의 두 배 크기 타겟. 처음 찍을 때 만든다 */
  let hdr: { rt: THREE.WebGLRenderTarget; buf: Uint16Array; lin: Float32Array; enc: Uint8Array } | null = null;
  const hdrRig = () => {
    if (hdr) return hdr;
    // half-float 비트 → 선형값(0~1 로 자름), 선형 0~1 → sRGB 8비트. 픽셀마다 계산하지 않으려고 표로 둔다
    const lin = new Float32Array(65536);
    for (let h = 0; h < 65536; h++) { const f = THREE.DataUtils.fromHalfFloat(h); lin[h] = f > 0 ? Math.min(1, f) : 0; }
    const enc = new Uint8Array(4096);
    for (let i = 0; i < 4096; i++) { const x = i / 4095; enc[i] = Math.round(255 * (x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055)); }
    hdr = {
      rt: new THREE.WebGLRenderTarget(W * 2, H * 2, { type: THREE.HalfFloatType, samples: 0, depthBuffer: true }),
      buf: new Uint16Array(W * 2 * H * 2 * 4),
      lin,
      enc,
    };
    return hdr;
  };
  const cam = new THREE.PerspectiveCamera(46, W / H, 0.05, 60);
  // 플래시. 카메라 바로 위에 달린 작은 등이라 그림자를 안 진다
  const flash = new THREE.PointLight(0xfff4e2, 5.5, 9, 1.7);
  const buf = new Uint8Array(W * H * 4);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  function shoot(
    clueId: string,
    group: THREE.Object3D,
    fallback: THREE.Vector3,
    from: THREE.Vector3,
  ): string {
    const hit = cache.get(clueId);
    if (hit) return hit;

    // 담을 것의 크기. look:'marker' 인 단서는 그릴 게 없으니(칠판·난로·사물함처럼
    // 가구가 이미 그 자리에 있다) 그 언저리를 통째로 담는다
    const box = visibleBox(group);
    const center = box ? box.getCenter(new THREE.Vector3()) : fallback.clone();
    const radius = box ? Math.max(0.28, box.getSize(new THREE.Vector3()).length() / 2) : 1.1;

    // 그 사람이 서 있던 방향에서. 뒤통수를 찍지 않으려면 방향이 있어야 한다
    const dir = new THREE.Vector3(from.x - center.x, 0, from.z - center.z);
    if (dir.lengthSq() < 1e-4) dir.set(0, 0, 1);
    dir.normalize();

    const dist = THREE.MathUtils.clamp(radius * 2.6, 0.75, 3.4);
    cam.position.copy(center).addScaledVector(dir, dist);
    cam.position.y = center.y + radius * 0.7 + 0.22;
    cam.lookAt(center);
    cam.updateProjectionMatrix();

    flash.position.copy(cam.position).addScaledVector(dir, -0.06);
    flash.position.y += 0.3;
    const lend = borrow?.();
    const saved = lend ? { p: lend.position.clone(), c: lend.color.clone(), i: lend.intensity, d: lend.distance, k: lend.decay } : null;
    if (lend) { lend.position.copy(flash.position); lend.color.copy(flash.color); lend.intensity = flash.intensity; lend.distance = flash.distance; lend.decay = flash.decay; }
    else scene.add(flash);

    const like = IS_GPU ? null : sceneTarget?.();
    const h = like?.texture.type === THREE.HalfFloatType ? hdrRig() : null;
    const prevTarget = renderer.getRenderTarget();
    renderer.setRenderTarget(h ? h.rt : rt);
    renderer.render(scene, cam);
    // WebGPU 는 픽셀을 비동기로만 읽는다 — 다 읽히면 캐시에 넣고, 그동안은 빈 값(photos.get 이 나중에 준다)
    const pixels = IS_GPU ? new Uint8Array(W * H * 4) : buf;
    const pending = IS_GPU ? readPixels(renderer, rt, W, H, pixels) : null;
    if (h) renderer.readRenderTargetPixels(h.rt, 0, 0, W * 2, H * 2, h.buf);
    else if (!IS_GPU) renderer.readRenderTargetPixels(rt, 0, 0, W, H, buf);
    renderer.setRenderTarget(prevTarget);
    if (lend && saved) { lend.position.copy(saved.p); lend.color.copy(saved.c); lend.intensity = saved.i; lend.distance = saved.d; lend.decay = saved.k; }
    else scene.remove(flash);

    const finish = () => {
      // WebGL 은 아래에서 위로 준다 — 뒤집는다. WebGPU 는 위에서 아래다
      const img = ctx.createImageData(W, H);
      if (h) {
        // 2×2 를 선형으로 평균 내고 sRGB 로 올린다
        const { buf: src, lin, enc } = h;
        const o = img.data;
        const row = W * 2 * 4;
        for (let y = 0, d = 0; y < H; y++) {
          let a = (H - 1 - y) * 2 * row;
          for (let x = 0; x < W; x++, a += 8, d += 4) {
            for (let c = 0; c < 3; c++) {
              o[d + c] = enc[((lin[src[a + c]] + lin[src[a + 4 + c]] + lin[src[a + row + c]] + lin[src[a + row + 4 + c]]) * (4095 / 4) + 0.5) | 0];
            }
            o[d + 3] = 255;
          }
        }
      } else for (let y = 0; y < H; y++) {
        const src = (IS_GPU ? y : H - 1 - y) * W * 4;
        img.data.set(pixels.subarray(src, src + W * 4), y * W * 4);
      }
      ctx.putImageData(img, 0, 0);
      stamp(ctx, cache.size + 1);
      const url = canvas.toDataURL('image/jpeg', 0.82);
      cache.set(clueId, url);
      return url;
    };
    if (pending) { void pending.then(finish).catch((e) => console.warn('[사진] 픽셀 읽기 실패', e)); return ''; }
    return finish();
  }

  return {
    shoot,
    get: (id) => cache.get(id) ?? null,
    dispose: () => {
      rt.dispose();
      hdr?.rt.dispose();
      cache.clear();
    },
  };
}

/** 증거 사진처럼 보이게 테두리와 표기를 얹는다. 안 얹으면 그냥 게임 스크린샷이다 */
function stamp(ctx: CanvasRenderingContext2D, n: number) {
  ctx.save();

  // 네 귀퉁이만 살짝 어둡게 — 필름으로 찍은 티
  const g = ctx.createRadialGradient(W / 2, H / 2, H * 0.32, W / 2, H / 2, H * 0.82);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, 'rgba(0,0,0,0.42)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, H - 30, W, 30);
  ctx.font = '15px monospace';
  ctx.fillStyle = '#c8b98a';
  ctx.textBaseline = 'middle';
  ctx.fillText(`증 제${String(n).padStart(2, '0')}호`, 14, H - 15);
  ctx.textAlign = 'right';
  ctx.fillText('2026-12-19  정선 폐교', W - 14, H - 15);

  ctx.restore();
}
